"use client"

import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { useTranslations } from "next-intl"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ChevronLeft, XCircle } from "lucide-react"
import { createClient } from "@/utils/supabase/client"
import { getAvatarUrl } from "@/lib/getAvatarUrl"
import { ChatComposer, type PhotoAttachment, type ChatComposerListing } from "./ChatComposer"
import { ChatListingBottomSheet } from "./ChatListingBottomSheet"
import { cn } from "@/lib/utils"
import {
  SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
  SEARCHABLE_DROPDOWN_OPTION_CLASS,
  searchableDropdownComposeSuggestionsClass,
} from "@/lib/dropdown-styles"
import {
  CHAT_CONVERSATION_HEADER_LAYER_CLASSNAME,
  CHAT_CONVERSATION_HEADER_SAFE_TOP_CLASS,
} from "@/components/chat/chat-conversation-chrome-styles"
import {
  ChatHeaderBackdrop,
  chatStickyChromeIconButtonClass,
  chatStickyHeaderPaddingTop,
  useChatHeaderChromeActive,
  useChatStickyHeaderHeight,
} from "@/components/chat/chat-sticky-header"
import { motion } from "framer-motion"
import { upsertChatRoom } from "@/lib/chat/chat-upsert"

type Profile = { id: string; username: string; avatar_url?: string | null }

type InitialDraft = { text?: string; imageFile?: File | null; listing?: any; order?: any } | null

export function ChatComposeTab({
  currentUserId,
  onOpenConversationAction,
  onCancelAction,
  dockToPanel = false,
}: {
  currentUserId: string
  onOpenConversationAction: (roomName: string, initialDraft?: InitialDraft, otherProfile?: { username: string; avatar_url?: string | null }) => void
  onCancelAction: () => void
  dockToPanel?: boolean
}) {
  const t = useTranslations("chat")
  const tInfo = useTranslations("profile.info")
  const supabase = useMemo(() => createClient(), [])

  const [username, setUsername] = useState("")
  const [selected, setSelected] = useState<Profile | null>(null)
  const [inputFocused, setInputFocused] = useState(false)
  const [searchResults, setSearchResults] = useState<Profile[]>([])
  const [searching, setSearching] = useState(false)

  const [message, setMessage] = useState("")
  const [selectedImages, setSelectedImages] = useState<PhotoAttachment[]>([])
  const [selectedListings, setSelectedListings] = useState<ChatComposerListing[]>([])
  const [showListingSelector, setShowListingSelector] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const usernameInputRef = useRef<HTMLInputElement | null>(null)
  const suggestionsRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const headerRef = useRef<HTMLElement | null>(null)

  const usernameLen = username.trim().length
  const isSuggestionsOpen = inputFocused && !selected && usernameLen >= 2

  useChatStickyHeaderHeight(headerRef)
  const chromeActive = useChatHeaderChromeActive(scrollRef, inputFocused || isSuggestionsOpen)
  const iconButtonClass = chatStickyChromeIconButtonClass(chromeActive)

  useEffect(() => {
    let mounted = true
    const term = username.trim()
    if (!term || selected || term.length < 2) {
      setSearchResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        if (!mounted) return
        const { data, error } = await supabase.rpc('get_public_profiles_with_followers', {
          p_current_user: currentUserId
        })
        if (error) throw error
        const filtered = (data || [])
          .filter((p: Profile) =>
            p.username.toLowerCase().includes(term.toLowerCase()) &&
            p.id !== currentUserId
          )
          .slice(0, 10)
        if (mounted) setSearchResults(filtered as Profile[])
      } catch {
        if (mounted) setSearchResults([])
      } finally {
        if (mounted) setSearching(false)
      }
    }, 200)
    return () => { mounted = false; clearTimeout(timer) }
  }, [currentUserId, selected, supabase, username])

  const handleSend = useCallback(async () => {
    const text = message.trim()
    if (!text && selectedImages.length === 0 && selectedListings.length === 0) return

    let other: Profile | null = selected
    if (!other && username.trim()) {
      const { data } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .eq('username', username.trim())
        .single()
      other = (data as Profile) || null
    }
    if (!other) {
      usernameInputRef.current?.focus()
      return
    }

    const roomName = [currentUserId, other.id].sort().join(':')
    const otherProfile = { username: other.username, avatar_url: other.avatar_url }

    const listing = selectedListings[0] ?? null
    const imageFile = selectedImages[0]?.file ?? null

    const initialDraft: InitialDraft = listing
      ? { text, listing }
      : imageFile
      ? { text, imageFile }
      : { text }

    selectedImages.forEach(p => URL.revokeObjectURL(p.previewUrl))
    setSelectedImages([])
    setSelectedListings([])
    setMessage("")

    onOpenConversationAction(roomName, initialDraft, otherProfile)
    upsertChatRoom({ roomName, participantIds: [currentUserId, other.id] }).catch(() => {})
  }, [currentUserId, message, onOpenConversationAction, selected, selectedImages, selectedListings, supabase, username])

  const handlePickUser = useCallback(async (user: Profile) => {
    const roomName = [currentUserId, user.id].sort().join(':')
    const text = message.trim()
    const listing = selectedListings[0] ?? null
    const imageFile = selectedImages[0]?.file ?? null

    const initialDraft: InitialDraft = listing
      ? { text, listing }
      : imageFile
      ? { text, imageFile }
      : (text ? { text } : null)
    const otherProfile = { username: user.username, avatar_url: user.avatar_url ?? null }

    selectedImages.forEach(p => URL.revokeObjectURL(p.previewUrl))
    setSelectedImages([])
    setSelectedListings([])
    setMessage("")
    setSelected(null)
    setUsername("")
    setInputFocused(false)

    onOpenConversationAction(roomName, initialDraft, otherProfile)
    upsertChatRoom({ roomName, participantIds: [currentUserId, user.id] }).catch(() => {})
  }, [currentUserId, message, onOpenConversationAction, selectedImages, selectedListings])

  const addImages = useCallback((files: FileList | null) => {
    if (!files) return
    const attachments: PhotoAttachment[] = Array.from(files).map(file => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }))
    setSelectedImages(prev => [...prev, ...attachments])
  }, [])

  const removeImage = useCallback((id: string) => {
    setSelectedImages(prev => {
      const target = prev.find(p => p.id === id)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter(p => p.id !== id)
    })
  }, [])

  const toggleListing = useCallback((listing: any) => {
    setSelectedListings(prev => {
      const exists = prev.find(l => l.id === listing.id)
      if (exists) return prev.filter(l => l.id !== listing.id)
      return [...prev, listing as ChatComposerListing]
    })
  }, [])

  const composerHeight = showListingSelector || selectedImages.length > 0 || selectedListings.length > 0 ? 280 : 82
  const dock = dockToPanel
  const layer = dock ? "absolute" : "fixed"

  return (
    <div
      className={cn(
        "relative flex h-full min-h-0 w-full flex-col bg-transparent text-foreground antialiased",
        dock && "overflow-hidden"
      )}
      style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}
    >
      <header
        ref={headerRef}
        data-chat-compose-header
        className={cn(
          CHAT_CONVERSATION_HEADER_LAYER_CLASSNAME,
          !dock && CHAT_CONVERSATION_HEADER_SAFE_TOP_CLASS,
          layer,
          dock ? "z-20" : "z-50",
        )}
      >
        <ChatHeaderBackdrop active={chromeActive} />
        <div className="relative z-1">
          <div
            className={cn(
              "mx-auto flex h-11 min-h-12 w-full shrink-0 items-center gap-2 px-3 md:px-4",
              !dock && "max-w-5xl",
            )}
          >
            {!dock ? (
              <button
                type="button"
                onClick={onCancelAction}
                className={iconButtonClass}
                aria-label={tInfo("goBack")}
              >
                <ChevronLeft className="h-5 w-5 text-foreground" />
              </button>
            ) : null}

            <span className="shrink-0 text-sm font-medium text-muted-foreground">To:</span>

            {selected ? (
              <Badge variant="secondary" className="flex items-center gap-1.5 px-2 py-1">
                <Avatar className="h-4 w-4">
                  <AvatarImage src={getAvatarUrl(selected.avatar_url || '') || undefined} alt={selected.username} />
                  <AvatarFallback className="text-[10px]">{selected.username.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium">{selected.username}</span>
                <button
                  type="button"
                  onClick={() => { setSelected(null); setUsername("") }}
                  className="rounded-full hover:text-destructive transition-colors"
                >
                  <XCircle className="h-3 w-3" />
                </button>
              </Badge>
            ) : (
              <div
                className="relative min-w-0 flex-1 cursor-text [-webkit-tap-highlight-color:transparent]"
                onClick={() => usernameInputRef.current?.focus()}
              >
                <Input
                  ref={usernameInputRef}
                  placeholder={t("search")}
                  aria-label="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => {
                    setTimeout(() => {
                      const ae = document.activeElement as HTMLElement | null
                      if (suggestionsRef.current && ae && suggestionsRef.current.contains(ae)) return
                      setInputFocused(false)
                    }, 0)
                  }}
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  autoCapitalize="none"
                  name="dm-compose-username"
                  inputMode="text"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-form-type="other"
                  enterKeyHint="next"
                  className="w-full appearance-none border-none bg-transparent px-0 py-0 shadow-none outline-hidden [-webkit-tap-highlight-color:transparent] placeholder:text-muted-foreground focus:outline-hidden focus:ring-0 focus-visible:outline-hidden focus-visible:ring-0 focus-visible:ring-offset-0"
                  style={{ fontSize: '16px' }}
                />
                {isSuggestionsOpen && (
                  <div
                    ref={suggestionsRef}
                    className={cn(
                      "absolute left-0 top-full z-1500 mt-2 overflow-y-auto p-2 shadow-2xl",
                      searchableDropdownComposeSuggestionsClass(),
                    )}
                  >
                    {searching ? (
                      <div className={cn("flex items-center gap-2 px-4 py-3 text-base", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)}>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-600 border-t-neutral-300" />
                        {t("searching")}
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className={cn("px-4 py-3 text-base", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)}>{t("noUsers")}</div>
                    ) : (
                      searchResults.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          className={cn("flex w-full items-center gap-3 text-left", SEARCHABLE_DROPDOWN_OPTION_CLASS)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handlePickUser(user)}
                        >
                          <Avatar className="h-8 w-8 shrink-0">
                            <AvatarImage src={getAvatarUrl(user.avatar_url || '') || undefined} alt={user.username} />
                            <AvatarFallback>{user.username.slice(0, 2).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <span className="min-w-0 truncate font-medium">{user.username}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          paddingTop: chatStickyHeaderPaddingTop(),
          paddingBottom: composerHeight,
        }}
      >
        <div className="flex h-full min-h-[200px] items-center justify-center">
          <p className="text-sm text-muted-foreground">{t("searchForSomeone")}</p>
        </div>
      </div>

      <motion.div
        className={cn(
          layer,
          "bottom-0 left-0 right-0 z-50 w-full bg-linear-to-t from-background via-background/70 to-transparent pointer-events-none"
        )}
        style={dock ? undefined : { bottom: 0 }}
        animate={{ height: composerHeight }}
        transition={{ type: "tween", duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      />

      <div
        className={cn(
          layer,
          "bottom-0 left-0 right-0 z-90 w-full px-4 pb-4 pt-4",
          !dock && "mx-auto max-w-5xl"
        )}
      >
        {showListingSelector && (
          <ChatListingBottomSheet
            currentUserId={currentUserId}
            selectedIds={selectedListings.map(l => l.id)}
            onToggle={toggleListing}
            onClose={() => setShowListingSelector(false)}
          />
        )}
        <ChatComposer
          value={message}
          onChange={setMessage}
          onSend={handleSend}
          onPhotoClick={() => fileInputRef.current?.click()}
          onOpenListingPicker={() => setShowListingSelector(true)}
          selectedImages={selectedImages}
          onRemoveImage={removeImage}
          selectedListings={selectedListings}
          onRemoveListing={(id) => setSelectedListings(prev => prev.filter(l => l.id !== id))}
          selectedOrder={null}
          onRemoveOrder={() => {}}
          listingPicker={null}
          disabled={false}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            addImages(e.target.files)
            e.target.value = ""
          }}
        />
      </div>
    </div>
  )
}
